import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClipboardList, Download, Link2, Plus, Trash2 } from 'lucide-react'
import { Modal } from '@symphenia/ui-components'
import {
  REGIE_LINE_KINDS,
  erfuelltRecht,
  regieSortiert,
  regieStunden,
  regieStundenAusMinuten,
  regieStundenText,
  type RegieInhalt,
  type RegieLineKind,
  type RegieVorbelegung,
  type RegieZeile,
  type SiteDocDetail,
} from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { FeldZeile, feldKlasse, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { cn } from '@/lib/utils'
import { regieDeviceCache, regieInDieAblage, regieLineAssign, regieLineCreate, regieLineDelete, regieLineUpdate, regiePrefill, updateSiteDoc } from '../../api/site-docs.api'
import type { ErpDetailProps } from '../registry'
import { RegieDiktat } from './RegieDiktat'

const inhaltVon = (d: SiteDocDetail): RegieInhalt => {
  const c = (d.content ?? {}) as Partial<RegieInhalt>
  return { beschreibung: c.beschreibung ?? '', anordnung: c.anordnung ?? null, zeilen: Array.isArray(c.zeilen) ? c.zeilen : [] }
}

const zeileAus = (d: SiteDocDetail, id: string) => inhaltVon(d).zeilen.find((z) => z.id === id)

/** Felder einer Zeile, wie die Speicherstelle sie führt. */
const alsDatensatz = (z: RegieZeile): Datensatz => ({
  description: z.description,
  quantity: Number(z.quantity),
  unitCode: z.unitCode,
  personName: z.personName ?? null,
  fromTime: z.fromTime ?? null,
  toTime: z.toTime ?? null,
  articleNumber: z.articleNumber ?? null,
  articleName: z.articleName ?? null,
  surchargeKey: z.surchargeKey ?? null,
})

/** Eine Zeile im ERP-Entwurf — feldweise beim Verlassen, über die Speicherstelle der Zeile (AP-36). */
function ZeileBearbeiten({ dok, zeile, zuschlaege, onGeaendert }: { dok: SiteDocDetail; zeile: RegieZeile; zuschlaege: { key: string; label: string }[]; onGeaendert: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const daten = useMemo(() => alsDatensatz(zeile), [zeile])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `regieZeile:${zeile.id}`,
    bezeichnung: t('siteDocs.regie.line', { text: zeile.description || t(`siteDocs.regie.groups.${zeile.kind}`) }),
    daten,
    schreibe: async (patch) => {
      const neu = await regieLineUpdate(dok.id, zeile.id!, patch)
      onGeaendert(neu)
      const z = zeileAus(neu, zeile.id!)
      return z ? alsDatensatz(z) : daten
    },
  }) as Speicherstelle<unknown>
  const stunden = zeile.kind === 'SERVICE'
  const beschreibung = useFeld<string | null>(stelle, 'description', { label: t('siteDocs.regie.description'), pflicht: true })
  const menge = useFeld<number | null>(stelle, 'quantity', { art: 'zahl', label: t('siteDocs.regie.quantity'), pflicht: true, stellen: 3 })
  const einheit = useFeld<string | null>(stelle, 'unitCode', { label: t('siteDocs.regie.unit'), pflicht: true })
  const person = useFeld<string | null>(stelle, 'personName', { label: t('siteDocs.regie.person') })
  const von = useFeld<string | null>(stelle, 'fromTime', { label: t('siteDocs.regie.from'), pruefe: (w) => (!w || /^\d{1,2}:\d{2}$/.test(w) ? null : 'HH:MM') })
  const bis = useFeld<string | null>(stelle, 'toTime', { label: t('siteDocs.regie.to'), pruefe: (w) => (!w || /^\d{1,2}:\d{2}$/.test(w) ? null : 'HH:MM') })
  const artikelNr = useFeld<string | null>(stelle, 'articleNumber', { label: t('siteDocs.regie.articleNumber') })
  const artikel = useFeld<string | null>(stelle, 'articleName', { label: t('siteDocs.regie.article') })
  const zuschlag = useFeld<string | null>(stelle, 'surchargeKey', { art: 'auswahl', label: t('siteDocs.regie.surcharge') })
  const zelle = 'px-2 py-1 align-top'
  return (
    <tr className="border-b border-border last:border-0">
      <td className={zelle}>
        <input className={feldKlasse} aria-label={t('siteDocs.regie.description')} {...beschreibung.props} />
      </td>
      {stunden ? (
        <>
          <td className={zelle}>
            <input className={feldKlasse} aria-label={t('siteDocs.regie.person')} {...person.props} />
          </td>
          <td className={`${zelle} whitespace-nowrap`}>
            <input className={`${feldKlasse} w-20`} placeholder="07:00" aria-label={t('siteDocs.regie.from')} {...von.props} />–<input className={`${feldKlasse} w-20`} placeholder="15:30" aria-label={t('siteDocs.regie.to')} {...bis.props} />
          </td>
          <td className={zelle}>
            <select className={feldKlasse} aria-label={t('siteDocs.regie.surcharge')} {...zuschlag.props}>
              <option value="">{t('siteDocs.regie.noSurcharge')}</option>
              {zuschlaege.map((z) => (
                <option key={z.key} value={z.key}>
                  {z.label}
                </option>
              ))}
            </select>
          </td>
        </>
      ) : (
        <td className={zelle} colSpan={3}>
          <div className="flex gap-1">
            <input className={`${feldKlasse} w-24`} placeholder={t('siteDocs.regie.articleNumber')} aria-label={t('siteDocs.regie.articleNumber')} {...artikelNr.props} />
            <input className={feldKlasse} placeholder={t('siteDocs.regie.article')} aria-label={t('siteDocs.regie.article')} {...artikel.props} />
          </div>
        </td>
      )}
      <td className={zelle}>
        <input className={`${feldKlasse} w-20 text-right`} inputMode="decimal" aria-label={t('siteDocs.regie.quantity')} {...menge.props} />
      </td>
      <td className={zelle}>
        {stunden ? <span className="inline-block py-1.5">h</span> : <input className={`${feldKlasse} w-16`} aria-label={t('siteDocs.regie.unit')} {...einheit.props} />}
      </td>
    </tr>
  )
}

/** „Buchung zuordnen" (AP-27 4.3): freie Buchungen des Tages auf dem Projekt und die schon verknüpften. */
function BuchungenWaehlen({ dok, titel, vorgewaehlt, onClose, onWahl }: { dok: SiteDocDetail; titel: string; vorgewaehlt: string[]; onClose: () => void; onWahl: (b: RegieVorbelegung[]) => Promise<void> }) {
  const { t } = useTranslation()
  const [liste, setListe] = useState<RegieVorbelegung[] | null>(null)
  const [wahl, setWahl] = useState<Set<string>>(new Set(vorgewaehlt))
  const [fehler, setFehler] = useState<string | null>(null)
  useEffect(() => {
    regiePrefill({ projectId: dok.projectId, projectRefId: dok.projectRefId, date: dok.documentDate.slice(0, 10) })
      .then(setListe)
      .catch((e) => setFehler(fehlertext(e, t)))
  }, [dok.projectId, dok.projectRefId, dok.documentDate, t])
  const waehlbar = (b: RegieVorbelegung) => b.status === 'FREE' || vorgewaehlt.includes(b.timeEntryId)
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={titel}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 rounded-md border px-4 text-sm">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
            onClick={async () => {
              try {
                await onWahl((liste ?? []).filter((b) => wahl.has(b.timeEntryId)))
              } catch (e) {
                setFehler(fehlertext(e, t))
              }
            }}
          >
            {t('siteDocs.regie.apply')}
          </button>
        </div>
      }
    >
      <div className="space-y-2 text-sm">
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
        {!dok.projectId && !dok.projectRefId && <p className="text-muted-foreground">{t('siteDocs.regie.noBookingsFree')}</p>}
        {liste && !liste.length && <p className="text-muted-foreground">{t('siteDocs.regie.noBookings')}</p>}
        <ul className="space-y-1">
          {liste?.map((b) => (
            <li key={b.timeEntryId}>
              <label className={cn('flex items-center gap-3 rounded-md border px-3 py-1.5', !waehlbar(b) && 'opacity-50')}>
                <input
                  type="checkbox"
                  disabled={!waehlbar(b)}
                  checked={wahl.has(b.timeEntryId)}
                  onChange={(e) =>
                    setWahl((w) => {
                      const n = new Set(w)
                      if (e.target.checked) n.add(b.timeEntryId)
                      else n.delete(b.timeEntryId)
                      return n
                    })
                  }
                />
                <span className="min-w-0 flex-1">
                  {b.personName} · {regieStundenText(regieStundenAusMinuten(b.minutes))} h
                  <span className="ml-2 text-xs text-muted-foreground">
                    {[b.activity, b.qualification].filter(Boolean).join(' · ')}
                    {b.status === 'BILLED' && ` · ${t('siteDocs.regie.billedAlready')}`}
                    {b.status === 'LINKED' && !vorgewaehlt.includes(b.timeEntryId) && ` · ${t('siteDocs.regie.linkedAlready', { nummer: b.linkedReport })}`}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  )
}

/** Beschreibung und Anordnung des ERP-Entwurfs — eine Speicherstelle, geschrieben als `content`. */
function KopfRegie({ dok, onGeaendert }: ErpDetailProps) {
  const { t } = useTranslation()
  const i = inhaltVon(dok)
  const daten = useMemo<Datensatz>(() => ({ beschreibung: i.beschreibung, von: i.anordnung?.von ?? null, funktion: i.anordnung?.funktion ?? null, am: i.anordnung?.am ?? null }), [i.beschreibung, i.anordnung?.von, i.anordnung?.funktion, i.anordnung?.am])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `regieKopf:${dok.id}`,
    bezeichnung: t('siteDocs.regie.head'),
    daten,
    schreibe: async (patch) => {
      const n = { ...daten, ...patch }
      const anordnung = n.von ? { von: n.von, funktion: n.funktion ?? null, am: n.am ?? null } : null
      const neu = await updateSiteDoc(dok.kind, dok.id, { content: { beschreibung: n.beschreibung ?? '', anordnung } })
      onGeaendert(neu)
      return n
    },
  }) as Speicherstelle<unknown>
  const beschreibung = useFeld<string | null>(stelle, 'beschreibung', { label: t('siteDocs.regie.whatDone') })
  const von = useFeld<string | null>(stelle, 'von', { label: t('siteDocs.regie.orderedBy') })
  const funktion = useFeld<string | null>(stelle, 'funktion', { label: t('siteDocs.regie.orderedFunction') })
  const am = useFeld<string | null>(stelle, 'am', { art: 'datum', label: t('siteDocs.regie.orderedOn') })
  return (
    <div className="space-y-1">
      <FeldZeile label={t('siteDocs.regie.whatDone')} status={beschreibung}>
        <textarea className={`${feldKlasse} min-h-16`} {...beschreibung.props} />
      </FeldZeile>
      <FeldZeile label={t('siteDocs.regie.orderedBy')} status={von}>
        <input className={feldKlasse} {...von.props} />
      </FeldZeile>
      <FeldZeile label={t('siteDocs.regie.orderedFunction')} status={funktion}>
        <input className={feldKlasse} {...funktion.props} />
      </FeldZeile>
      <FeldZeile label={t('siteDocs.regie.orderedOn')} status={am}>
        <input type="date" className={feldKlasse} {...am.props} />
      </FeldZeile>
    </div>
  )
}

/**
 * AP-27 9. — **Detailteil Regiebericht im ERP.** Im Entwurf: Beschreibung, Anordnung und die Zeilen je
 * Art, feldweise (AP-36), „Aus Buchungen übernehmen". In jedem Zustand: Herkunft und Abrechnungsstand je
 * Zeile, mit `sitedocs.regie.assign` die Buchungen je Stundenzeile („Buchung zuordnen"), auch nach dem
 * Abschluss — außer an abgerechneten Zeilen.
 */
export function RegieErpDetail({ dok, onGeaendert }: ErpDetailProps) {
  const { t } = useTranslation()
  const { user, hasModule } = useAuthStore()
  const darfZuordnen = erfuelltRecht('sitedocs.regie.assign', user?.permissions)
  const entwurf = dok.status === 'DRAFT' && !dok.heldByUserId && dok.can.edit
  const inhalt = inhaltVon(dok)
  const zeilen = regieSortiert(inhalt.zeilen)
  const [fehler, setFehler] = useState<string | null>(null)
  const [uebernehmen, setUebernehmen] = useState(false)
  const [zuordnen, setZuordnen] = useState<RegieZeile | null>(null)
  const zuschlaege = useMemo(() => {
    const vorhanden = new Map<string, string>()
    for (const z of inhalt.zeilen) if (z.surchargeKey && z.surchargeLabel) vorhanden.set(z.surchargeKey, z.surchargeLabel)
    return [...vorhanden].map(([key, label]) => ({ key, label }))
  }, [inhalt.zeilen])
  const [listeZuschlaege, setListeZuschlaege] = useState<{ key: string; label: string }[]>([])
  useEffect(() => {
    if (!entwurf) return
    regieDeviceCache({ projectId: dok.projectId, projectRefId: dok.projectRefId })
      .then((v) => setListeZuschlaege(v.surcharges))
      .catch(() => setListeZuschlaege([]))
  }, [entwurf, dok.projectId, dok.projectRefId])
  const alleZuschlaege = listeZuschlaege.length ? listeZuschlaege : zuschlaege

  const tue = async (f: () => Promise<SiteDocDetail>) => {
    setFehler(null)
    try {
      onGeaendert(await f())
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  // AP-27 7.: nur freigegeben mit Unterschrift des Kunden, nur mit Belegwesen, nur offene Zeilen.
  const [abgelegt, setAbgelegt] = useState<string | null>(null)
  const offeneZeilen = zeilen.filter((z) => !z.billedAt)
  const ablegbar =
    dok.status === 'FINAL' &&
    dok.signatures.some((s) => s.role === 'CUSTOMER') &&
    offeneZeilen.length > 0 &&
    hasModule('CORRESPONDENCE') &&
    erfuelltRecht('sitedocs.regie.bill', user?.permissions)
  const inDieAblage = async () => {
    setFehler(null)
    setAbgelegt(null)
    try {
      const r = await regieInDieAblage({ documentIds: [dok.id] })
      setAbgelegt(t('siteDocs.regie.clipboardDone', { count: r.lines }))
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  const stunden = regieStunden(inhalt.zeilen)
  const kopfZelle = 'px-2 py-1 text-left text-xs font-medium text-muted-foreground'

  return (
    <div className="space-y-4">
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
      {ablegbar && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="inline-flex h-8 items-center gap-1 rounded-md border px-3 text-sm" onClick={() => void inDieAblage()}>
            <ClipboardList className="h-4 w-4" /> {t('siteDocs.regie.toClipboard')}
          </button>
          {abgelegt ? <span className="text-sm text-muted-foreground">{abgelegt}</span> : <span className="text-xs text-muted-foreground">{t('siteDocs.regie.toClipboardHint', { count: offeneZeilen.length })}</span>}
        </div>
      )}
      {/* Eingefroren steht der Inhalt im kanonischen Stand darüber; hier nur Herkunft, Buchungen, Abrechnung. */}
      {/* AP-27 18.: Diktat in Zeilen — nur mit gebuchtem und eingeschaltetem `SITE_DOCS.AI`. */}
      {entwurf && dok.can.dictate && <RegieDiktat dok={dok} inhalt={inhalt} onGeaendert={onGeaendert} />}
      {entwurf && <KopfRegie dok={dok} onGeaendert={onGeaendert} />}

      {REGIE_LINE_KINDS.map((kind: RegieLineKind) => {
        const liste = zeilen.filter((z) => z.kind === kind)
        if (!entwurf && !liste.length) return null
        return (
          <section key={kind} className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-medium">{t(`siteDocs.regie.groups.${kind}`)}</h3>
              {entwurf && (
                <div className="flex gap-2">
                  {kind === 'SERVICE' && (
                    <button type="button" className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs" onClick={() => setUebernehmen(true)}>
                      <Download className="h-3.5 w-3.5" /> {t('siteDocs.regie.fromBookings')}
                    </button>
                  )}
                  <button type="button" className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs" onClick={() => void tue(() => regieLineCreate(dok.id, { kind, quantity: 0, unitCode: kind === 'SERVICE' ? 'h' : '' }))}>
                    <Plus className="h-3.5 w-3.5" /> {t('siteDocs.regie.addLine')}
                  </button>
                </div>
              )}
            </div>
            {entwurf ? (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      <th className={kopfZelle}>{t('siteDocs.regie.description')}</th>
                      {kind === 'SERVICE' ? (
                        <>
                          <th className={kopfZelle}>{t('siteDocs.regie.person')}</th>
                          <th className={kopfZelle}>{t('siteDocs.regie.time')}</th>
                          <th className={kopfZelle}>{t('siteDocs.regie.surcharge')}</th>
                        </>
                      ) : (
                        <th className={kopfZelle} colSpan={3}>
                          {t('siteDocs.regie.article')}
                        </th>
                      )}
                      <th className={kopfZelle}>{t('siteDocs.regie.quantity')}</th>
                      <th className={kopfZelle}>{t('siteDocs.regie.unit')}</th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {liste.map((z) => (
                      <ZeileMitAktion key={z.id ?? z.clientId} dok={dok} zeile={z} zuschlaege={alleZuschlaege} onGeaendert={onGeaendert} onLoeschen={() => void tue(() => regieLineDelete(dok.id, z.id!))} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <ul className="divide-y rounded-md border text-sm">
                {liste.map((z) => (
                  <li key={z.id ?? z.clientId} className="flex flex-wrap items-center gap-2 px-3 py-1.5">
                    <span className="min-w-0 flex-1">
                      <span className="font-medium">{kind === 'SERVICE' ? z.personName ?? z.description : z.articleName ?? z.description}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {[kind === 'SERVICE' ? z.description : null, z.qualificationName, z.fromTime && z.toTime ? `${z.fromTime}–${z.toTime}` : null, z.surchargeLabel, z.articleNumber].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <span className="tabular-nums">
                      {String(Number(z.quantity)).replace('.', ',')} {z.unitCode}
                    </span>
                    <span className="text-xs text-muted-foreground">{t(`siteDocs.regie.origins.${z.origin ?? 'MANUAL'}`)}</span>
                    {z.billedAt ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">{t('siteDocs.regie.billed')}</span>
                    ) : (
                      kind === 'SERVICE' &&
                      (dok.projectId || dok.projectRefId) && (
                        <span className={cn('text-xs', z.linkedBookings?.length ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-300')}>
                          {z.linkedBookings?.length ? t('siteDocs.regie.bookingsLinked', { count: z.linkedBookings.length }) : t('siteDocs.regie.bookingMissing')}
                        </span>
                      )
                    )}
                    {kind === 'SERVICE' && darfZuordnen && !z.billedAt && dok.status !== 'VOIDED' && dok.status !== 'DECLINED' && (dok.projectId || dok.projectRefId) && (
                      <button type="button" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" onClick={() => setZuordnen(z)}>
                        <Link2 className="h-3.5 w-3.5" /> {t('siteDocs.regie.assignBooking')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )
      })}

      {entwurf && stunden.gesamt > 0 && (
        <p className="text-sm">
          <strong>
            {t('siteDocs.regie.totalHours')}: {regieStundenText(stunden.gesamt)} h
          </strong>
          {(stunden.jeZuschlag.length > 1 || stunden.jeZuschlag[0]?.zuschlag) && (
            <span className="ml-2 text-muted-foreground">{stunden.jeZuschlag.map((x) => `${x.zuschlag ?? t('siteDocs.regie.noSurcharge')}: ${regieStundenText(x.stunden)} h`).join(' · ')}</span>
          )}
        </p>
      )}

      {uebernehmen && (
        <BuchungenWaehlen
          dok={dok}
          titel={t('siteDocs.regie.fromBookings')}
          vorgewaehlt={[]}
          onClose={() => setUebernehmen(false)}
          onWahl={async (gewaehlt) => {
            let letzt: SiteDocDetail | null = null
            for (const b of gewaehlt) {
              letzt = await regieLineCreate(dok.id, {
                kind: 'SERVICE',
                description: b.activity?.trim() || t('siteDocs.regie.workingTime'),
                quantity: regieStundenAusMinuten(b.minutes),
                unitCode: 'h',
                personName: b.personName,
                personUserId: b.userId,
                qualificationName: b.qualification ?? undefined,
                timeEntryIds: [b.timeEntryId],
              })
            }
            if (letzt) onGeaendert(letzt)
            setUebernehmen(false)
          }}
        />
      )}
      {zuordnen && (
        <BuchungenWaehlen
          dok={dok}
          titel={t('siteDocs.regie.assignBooking')}
          vorgewaehlt={zuordnen.linkedBookings ?? []}
          onClose={() => setZuordnen(null)}
          onWahl={async (gewaehlt) => {
            onGeaendert(await regieLineAssign(dok.id, zuordnen.id!, { timeEntryIds: gewaehlt.map((b) => b.timeEntryId) }))
            setZuordnen(null)
          }}
        />
      )}
    </div>
  )
}

function ZeileMitAktion({ dok, zeile, zuschlaege, onGeaendert, onLoeschen }: { dok: SiteDocDetail; zeile: RegieZeile; zuschlaege: { key: string; label: string }[]; onGeaendert: (neu: SiteDocDetail) => void; onLoeschen: () => void }) {
  const { t } = useTranslation()
  return (
    <>
      <ZeileBearbeiten dok={dok} zeile={zeile} zuschlaege={zuschlaege} onGeaendert={onGeaendert} />
      <tr className="border-b border-border last:border-0">
        <td colSpan={7} className="px-2 pb-1 text-right">
          {zeile.origin === 'BOOKING' && <span className="mr-3 text-xs text-muted-foreground">{t('siteDocs.regie.origins.BOOKING')}</span>}
          <button type="button" className="inline-flex items-center gap-1 text-xs text-red-700 hover:underline" onClick={onLoeschen}>
            <Trash2 className="h-3.5 w-3.5" /> {t('siteDocs.regie.removeLine')}
          </button>
        </td>
      </tr>
    </>
  )
}
